import type { StateVector } from "../math/state_vector";
import { Vector3d } from "../math/vector3d";
import {
  type CelestialBodyData,
  BodyType,
  computeBodyState,
} from "./celestial_body_data";

/**
 * Registry of all celestial bodies in the simulation.
 * Provides lookup by name or NAIF ID, hierarchy traversal,
 * and position computation at arbitrary times.
 */
export class BodyRegistry {
  private readonly byName = new Map<string, CelestialBodyData>();
  private readonly byNaif = new Map<number, CelestialBodyData>();

  register(body: CelestialBodyData): void {
    this.byName.set(body.name, body);
    this.byNaif.set(body.naifId, body);
  }

  getByName(name: string): CelestialBodyData | undefined {
    return this.byName.get(name);
  }

  getByNaif(naifId: number): CelestialBodyData | undefined {
    return this.byNaif.get(naifId);
  }

  get allBodies(): CelestialBodyData[] {
    return Array.from(this.byName.values());
  }

  get count(): number {
    return this.byName.size;
  }

  /** Get the parent body of the given body. */
  getParent(body: CelestialBodyData): CelestialBodyData | undefined {
    if (!body.parentBodyName) return undefined;
    return this.getByName(body.parentBodyName);
  }

  /** Get all children (bodies that orbit the given body). */
  getChildren(body: CelestialBodyData): CelestialBodyData[] {
    const children: CelestialBodyData[] = [];
    for (const b of this.byName.values()) {
      if (b.parentBodyName === body.name) {
        children.push(b);
      }
    }
    return children;
  }

  /** Get GM of a body by name. */
  getGM(bodyName: string): number {
    const body = this.getByName(bodyName);
    return body?.gm ?? 0;
  }

  /**
   * Compute heliocentric positions of all bodies at a given time.
   * Returns a Map mapping body name to heliocentric position in km.
   */
  computeAllPositions(jd: number): Map<string, Vector3d> {
    const positions = new Map<string, Vector3d>();

    // First pass: bodies orbiting the Sun (stars + planets)
    for (const body of this.byName.values()) {
      if (body.bodyType === BodyType.Star) {
        positions.set(body.name, Vector3d.Zero);
      } else if (body.parentBodyName === "Sun") {
        const state = computeBodyState(body, jd, this.getGM("Sun"));
        positions.set(body.name, state.position);
      }
    }

    // Second pass: moons (relative to parent + parent heliocentric)
    for (const body of this.byName.values()) {
      if (body.bodyType === BodyType.Moon && body.parentBodyName !== "Sun") {
        const parent = this.getByName(body.parentBodyName!);
        if (parent && positions.has(parent.name)) {
          const state = computeBodyState(body, jd, parent.gm);
          positions.set(body.name, positions.get(parent.name)!.add(state.position));
        }
      }
    }

    return positions;
  }

  /**
   * Compute the state vector of a body relative to its parent at a given time.
   */
  computeBodyState(bodyName: string, jd: number): StateVector {
    const body = this.getByName(bodyName);
    if (!body) {
      return { position: Vector3d.Zero, velocity: Vector3d.Zero, time: jd, centralBody: "" };
    }
    if (body.bodyType === BodyType.Star) {
      return { position: Vector3d.Zero, velocity: Vector3d.Zero, time: jd, centralBody: body.name };
    }

    const parent = this.getByName(body.parentBodyName!);
    if (!parent) {
      return { position: Vector3d.Zero, velocity: Vector3d.Zero, time: jd, centralBody: body.name };
    }

    return computeBodyState(body, jd, parent.gm);
  }

  /**
   * Find the dominant gravitational body for a given heliocentric position.
   * Checks all bodies' SOI radii.
   */
  findDominantBody(heliocentricPos: Vector3d, jd: number): CelestialBodyData {
    const positions = this.computeAllPositions(jd);
    let dominant = this.getByName("Sun")!;
    let smallestSOI = Infinity;

    for (const body of this.byName.values()) {
      if (body.bodyType === BodyType.Star) continue;
      const pos = positions.get(body.name);
      if (!pos) continue;

      const dist = heliocentricPos.subtract(pos).magnitude;
      if (dist < body.soiRadius && body.soiRadius < smallestSOI) {
        dominant = body;
        smallestSOI = body.soiRadius;
      }
    }

    return dominant;
  }

  /**
   * Factory: create a BodyRegistry from a JSON API response.
   * Expects an array of body objects with matching field names.
   */
  static fromApiResponse(json: CelestialBodyData[]): BodyRegistry {
    const registry = new BodyRegistry();
    for (const body of json) {
      registry.register(body);
    }
    return registry;
  }
}
